import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { Typography, Collapse } from '@material-ui/core';
import { useHistory } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import isEqual from 'lodash.isequal';

import masterServiceApi from '../../../apis/master-services';
import { SpaceBetweenContainer, HealtheraButton, InputField } from '../../../healtheraComponents';
import { RadioGroup, RichTextEditor, ImageUpload } from '../../Shared/Form';
import ImagePicker from '../components/ServiceDetails/ImagePicker';
import Keywords from '../components/ServiceDetails/Keywords';
import { FormContainer, Section, Column, ErrorMessage } from '../styled-components';
import Categories from './Categories';
import ConsultationSettings from './ConsultationSettings';
import useLocationQuery from '../../../hooks/useQuery';
import { getFormData } from '../../../utils/formatters';
import DeleteButton from './components/DeleteButton/DeleteButton';
import CancelButton from './components/CancelButton';
import { queryClient } from '../../QueryClientProvider/QueryClientProvider';

export default function UkServiceForm({ service, avatars, categories }) {
  const history = useHistory();
  const {
    query: { type: typeFromQuery }
  } = useLocationQuery();
  const { enqueueSnackbar } = useSnackbar();
  const isExistingService = Boolean(service?._id);

  const avatarUrlByTitle = avatars.reduce((acc, avatar) => {
    acc[avatar.name] = avatar.url;
    return acc;
  }, {});

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      title: service?.title || '',
      description_short: service?.description_short || '',
      description_html: service?.description_html || '',
      type: service?.type || typeFromQuery || 'NHS',
      avatar: service?.avatar || '',
      keywords: service?.keywords || [],
      categories: service?.categories || [],
      slider_banner: null,
      description_banner: null,
      consultation: service?.consultation || {
        countries: [],
        local_consultation_method: {},
        national_consultation_method: {},
        payment_type: 'paid',
        price: '',
        conditions: []
      }
    }
  });

  const [uploadedSliderBanner] = watch('slider_banner') || [];
  const sliderBanner = uploadedSliderBanner ? URL.createObjectURL(uploadedSliderBanner) : service?.slider_banner_url;

  const [uploadedDescriptionBanner] = watch('description_banner') || [];
  const descriptionBanner = uploadedDescriptionBanner
    ? URL.createObjectURL(uploadedDescriptionBanner)
    : service?.description_banner_url;

  const { mutateAsync: upsertMasterService } = useMutation({
    mutationFn: masterServiceApi.upsertMasterService,
    onSuccess: updatedService => {
      queryClient.setQueryData(['master-service', updatedService._id], updatedService);
      reset(updatedService);
      enqueueSnackbar(`Service successfully ${service ? 'updated' : 'created'}`, { variant: 'success' });
      if (!isExistingService) history.push(updatedService._id);
    }
  });

  async function onSubmit(data) {
    const updatePayload = {};
    if (data.title && data.title.trim() !== service?.title) {
      updatePayload.title = data.title.trim();
    }

    if (data.description_short && data.description_short.trim() !== service?.description_short) {
      updatePayload.description_short = data.description_short.trim();
    }

    if (data.description_html && data.description_html !== service?.description_html) {
      updatePayload.description_html = data.description_html;
    }

    if (data.type && data.type !== service?.type) updatePayload.type = data.type;
    if (data.avatar && data.avatar !== service?.avatar) updatePayload.avatar = data.avatar;
    if (data.keywords && !isEqual(data.keywords, service?.keywords)) {
      updatePayload.keywords = data.keywords;
    }

    if (data.categories && !isEqual(data.categories, service?.categories)) {
      updatePayload.categories = data.categories;
    }

    if (data.slider_banner) updatePayload.slider_banner = data.slider_banner[0];
    if (data.description_banner) updatePayload.description_banner = data.description_banner[0];

    if (data.type === 'Consultation') {
      const consultationPayload = {};
      if (data.consultation.countries && !isEqual(data.consultation.countries, service?.consultation?.countries)) {
        consultationPayload.countries = data.consultation.countries;
      }

      if (
        data.consultation.local_consultation_method &&
        !isEqual(data.consultation.local_consultation_method, service?.consultation?.local_consultation_method)
      ) {
        consultationPayload.local_consultation_method = data.consultation.local_consultation_method;
      }

      if (
        data.consultation.national_consultation_method &&
        !isEqual(data.consultation.national_consultation_method, service?.consultation?.national_consultation_method)
      ) {
        consultationPayload.national_consultation_method = data.consultation.national_consultation_method;
      }

      if (data.consultation.payment_type && data.consultation.payment_type !== service?.consultation?.payment_type) {
        consultationPayload.payment_type = data.consultation.payment_type;
      }

      if (!Number.isNaN(Number(data.consultation.price))) {
        const numericPrice = Number(data.consultation.price);
        if (numericPrice !== service?.consultation?.price) consultationPayload.price = numericPrice;
      }

      if (data.consultation.conditions && !isEqual(data.consultation.conditions, service?.consultation?.conditions)) {
        consultationPayload.conditions = data.consultation.conditions;
      }

      if (Object.keys(consultationPayload).length) {
        updatePayload.consultation = consultationPayload;
      }
    }

    if (!Object.keys(updatePayload).length) {
      enqueueSnackbar('No updates were made!', { variant: 'error' });
      return;
    }

    const formData =
      updatePayload.slider_banner || updatePayload.description_banner ? getFormData(updatePayload) : null;

    try {
      await upsertMasterService({ _id: service?._id, updatePayload, formData });
    } catch (error) {
      // This is handled in mutation
    }
  }

  function SubmitButton() {
    return (
      <HealtheraButton onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
        {isExistingService ? 'Save Changes' : 'Create Service'}
      </HealtheraButton>
    );
  }

  return (
    <Container>
      <SpaceBetweenContainer>
        <Typography variant="h5">Service settings</Typography>
        <SubmitButton />
      </SpaceBetweenContainer>
      <FormContainer>
        <Column>
          <Section>
            <header>Primary details</header>
            <Controller
              control={control}
              name="title"
              rules={{
                required: { value: true, message: 'Service name is required' },
                pattern: { value: /\S/, message: 'Service name must be non empty string' }
              }}
              render={({ field: { ref, ...otherField } }) => (
                <div>
                  <InputField
                    {...otherField}
                    margintop="0"
                    label="Service name"
                    inputRef={ref}
                    required
                    error={errors?.title?.message}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="type"
              render={({ field: { ref, ...otherField } }) => (
                <RadioGroup
                  {...otherField}
                  label="Type"
                  options={[
                    { label: 'NHS', value: 'NHS' },
                    { label: 'Private', value: 'Private' },
                    { label: 'Consultation', value: 'Consultation' }
                  ]}
                />
              )}
            />
          </Section>
          <Collapse in={watch('type') === 'Consultation'} unmountOnExit>
            <ConsultationSettings setValue={setValue} watch={watch} control={control} />
          </Collapse>
          <Section>
            <header>Description</header>
            <Controller
              control={control}
              name="description_short"
              rules={{
                required: 'One-line description is required',
                pattern: { value: /\S/, message: 'One-line description must be non empty string' },
                maxLength: 'One-line description should not exceed 300 characters'
              }}
              render={({ field: { ref, ...otherField } }) => (
                <div style={{ position: 'relative' }}>
                  <InputField
                    {...otherField}
                    margintop="0"
                    label="One-line description"
                    inputRef={ref}
                    required
                    limit={300}
                    error={errors?.description_short?.message}
                  />
                </div>
              )}
            />
            <div>
              <Typography style={{ fontSize: '13px', color: '#757575', marginBottom: '12px' }}>
                Full description *
              </Typography>
              <Controller
                control={control}
                name="description_html"
                rules={{ required: 'Full description is required' }}
                render={({ field: { value, onChange } }) => <RichTextEditor value={value} onChange={onChange} />}
              />
              {errors.description_html && <ErrorMessage>{errors.description_html.message}</ErrorMessage>}
            </div>
          </Section>
          <Section>
            <header>Categories</header>
            <Controller
              control={control}
              name="categories"
              render={({ field: { value, onChange } }) => (
                <Categories categories={categories} value={value} onChange={onChange} />
              )}
            />
          </Section>
          <Section>
            <header>Keywords</header>
            <Controller
              control={control}
              name="keywords"
              render={({ field: { value, onChange } }) => <Keywords value={value} onChange={onChange} />}
            />
          </Section>
        </Column>
        <Column>
          <Section>
            <header>Icon</header>
            <div>
              <Controller
                control={control}
                name="avatar"
                rules={{ required: { value: false } }}
                render={({ field: { name, value, onChange } }) => (
                  <ImagePicker
                    name={name}
                    image={{
                      src: avatarUrlByTitle[value],
                      alt: service?.title,
                      name: value
                    }}
                    images={avatars}
                    onChange={avatar => onChange(avatar.name)}
                  />
                )}
              />
              {errors.avatar && <ErrorMessage>{errors.avatar.message}</ErrorMessage>}
            </div>
          </Section>
          <Section>
            <header>Slider banner</header>
            <ImageUpload
              imageUrl={sliderBanner}
              replace={Boolean(service?.slider_banner_url)}
              inputProps={register('slider_banner', {
                required: service?.slider_banner_url ? false : 'Slider banner is required'
              })}
              error={errors?.slider_banner?.message}
            />
          </Section>
          <Section>
            <header>Description banner</header>
            <ImageUpload
              imageUrl={descriptionBanner}
              replace={Boolean(service?.description_banner_url)}
              inputProps={register('description_banner', {
                required: service?.description_banner_url ? false : 'Description banner is required'
              })}
              error={errors?.description_banner?.message}
            />
          </Section>
        </Column>
        <Section
          style={{ gridColumn: 'span 2', alignItems: 'end', flexDirection: 'row', justifyContent: 'space-between' }}
        >
          {isExistingService ? <DeleteButton service={service} /> : <CancelButton />}
          <SubmitButton />
        </Section>
      </FormContainer>
    </Container>
  );
}

const Container = styled.div`
  margin: 40px auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
